import { EI_TABLE_DEFAULTS, sortIcon } from '../constants/ei-table.constants'
import { IDropdownConfig } from '@app/shared/models/dropdown.model'
import { EiTableColumn } from '../models/ei-table.model'
import { sortType } from '@app/shared/enums/ei-table.enum'

/**
 * Check if a column is the internal actions column.
 * Actions column is always visible and cannot be managed.
 */
export function isActionsColumn(column: EiTableColumn): boolean {
    return column.key === EI_TABLE_DEFAULTS.actionsColumnKey
}

/**
 * Check if a column is the download button column.
 * Download column is always visible and cannot be managed.
 */
export function isDownloadColumn(column: EiTableColumn): boolean {
    return column.cellTemplate === 'downloadButton'
}

/**
 * Get visible columns based on column configuration and selected keys.
 * Always includes actions column and mandatory columns, regardless of selection.
 * If no visibleColumnKeys provided, returns all columns with visible !== false.
 */
export function getVisibleColumns(
    allColumns: EiTableColumn[],
    visibleColumnKeys: string[],
): EiTableColumn[] {
    if (!allColumns.length) return []

    const defaultVisibleColumns = allColumns.filter((column) => column.visible !== false)
    if (!visibleColumnKeys.length) return defaultVisibleColumns

    const selectedKeySet = new Set(visibleColumnKeys)
    // Always include actions column, download column, and mandatory columns, even if not in selection
    return defaultVisibleColumns.filter(
        (column) =>
            isActionsColumn(column) ||
            isDownloadColumn(column) ||
            column.mandatory ||
            selectedKeySet.has(column.key),
    )
}

/**
 * Get manageable column keys (excludes actions column).
 * Returns keys of all visible columns that can be toggled in "Manage columns".
 */
export function getManageableColumnKeys(allColumns: EiTableColumn[]): string[] {
    return allColumns
        .filter(
            (column) =>
                column.visible !== false && !isActionsColumn(column) && !isDownloadColumn(column),
        )
        .map((column) => String(column.key))
}

/**
 * Get mandatory column keys (excludes actions column).
 * Mandatory columns are always visible and cannot be deselected.
 */
export function getMandatoryColumnKeys(allColumns: EiTableColumn[]): string[] {
    return allColumns
        .filter(
            (column) =>
                column.visible !== false &&
                !isActionsColumn(column) &&
                !isDownloadColumn(column) &&
                column.mandatory,
        )
        .map((column) => String(column.key))
}

/**
 * Sync selected visible columns model, ensuring mandatory columns are always included.
 * Validates stored keys against manageable columns and merges with mandatory columns.
 */
export function syncSelectedVisibleColumnsModel(
    allColumns: EiTableColumn[],
    visibleColumnKeys: string[],
): string[] {
    const manageableDefaultKeys = getManageableColumnKeys(allColumns)
    const mandatoryKeys = getMandatoryColumnKeys(allColumns)
    const allowedKeySet = new Set(manageableDefaultKeys)

    const storedKeys = (visibleColumnKeys?.length ? visibleColumnKeys : manageableDefaultKeys).map(
        String,
    )
    const validStoredKeys = storedKeys.filter((key) => allowedKeySet.has(key))

    // Always include mandatory columns, use valid stored keys or fallback to defaults
    return Array.from(
        new Set([
            ...mandatoryKeys,
            ...(validStoredKeys.length ? validStoredKeys : manageableDefaultKeys),
        ]),
    )
}

/**
 * Build manage columns dropdown configuration.
 * Excludes actions column and disables mandatory columns.
 */
export function buildManageColumnsConfig(
    allColumns: EiTableColumn[],
    manageColumnsLabel: string,
    manageColumnsPlaceholder: string,
): IDropdownConfig {
    return {
        label: manageColumnsLabel,
        placeholder: manageColumnsPlaceholder,
        isMultiSelect: true,
        enableSearch: false,
        showSelectAll: true,
        options: allColumns
            // Exclude internal actions column from "Manage columns"
            .filter(
                (column) =>
                    column.visible !== false &&
                    !isActionsColumn(column) &&
                    !isDownloadColumn(column),
            )
            .map((column) => ({
                id: column.key,
                value: column.key,
                label: column.header,
                isDisabled: !!column.mandatory, // Mandatory columns are disabled (always selected)
            })),
    }
}

/**
 * Build filter dropdown configurations for all columns with select filters.
 * Returns a map of column keys to their dropdown configurations.
 */
export function buildFilterDropdownConfigs(
    allColumns: EiTableColumn[],
): Record<string, IDropdownConfig> {
    const filterConfigs: Record<string, IDropdownConfig> = {}
    for (const column of allColumns) {
        if (column.filterType !== 'select' || !column.filterOptions?.length) continue
        if (isActionsColumn(column)) continue
        filterConfigs[column.key] = {
            placeholder: column.header,
            isMultiSelect: true,
            enableSearch: true,
            options: column.filterOptions.map((option) => ({
                id: `${column.key}:${option.value}`,
                value: option.value,
                label: option.label,
            })),
        }
    }
    return filterConfigs
}

/**
 * Get sort icon name based on current sort state.
 * Returns Material Icons name for the appropriate sort state.
 */
export function getSortIcon(
    columnKey: string,
    sortActiveKey: string,
    sortDirection: string,
): string {
    if (sortActiveKey !== columnKey) return sortIcon.default
    if (sortDirection === sortType.ASC) return sortIcon.asc
    if (sortDirection === sortType.DESC) return sortIcon.desc
    return 'unfold_more'
}
